import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { User, Building2, X as XIcon, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import AdminFeaturedRecsManager from './AdminFeaturedRecsManager';

const BACKEND_URL = '';

export function AdminRecommendationsTab() {
  const [adminRecs, setAdminRecs] = useState([]);
  const [recUserSearch, setRecUserSearch] = useState('');
  const [recUserResults, setRecUserResults] = useState([]);
  const [recSelectedUser, setRecSelectedUser] = useState(null);
  const [recCompanySearch, setRecCompanySearch] = useState('');
  const [recCompanyResults, setRecCompanyResults] = useState([]);
  const [recSelectedCompany, setRecSelectedCompany] = useState(null);
  const [recNote, setRecNote] = useState('');
  const [recSubmitting, setRecSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/recommendations`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { recommendations: [] })
      .then(d => setAdminRecs(d.recommendations || []))
      .catch(() => {});
  }, []);

  const searchUsers = async (query) => {
    setRecUserSearch(query);
    if (query.length >= 2) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/users`, { credentials: 'include' });
        if (res.ok) {
          const allUsers = await res.json();
          setRecUserResults(allUsers.filter(u =>
            u.name?.toLowerCase().includes(query.toLowerCase()) ||
            u.email?.toLowerCase().includes(query.toLowerCase())
          ).slice(0, 8));
        }
      } catch {}
    } else {
      setRecUserResults([]);
    }
  };

  const searchCompanies = async (query) => {
    setRecCompanySearch(query);
    if (query.length >= 2) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/companies/search?q=${encodeURIComponent(query)}`, { credentials: 'include' });
        if (res.ok) setRecCompanyResults(await res.json());
      } catch {}
    } else {
      setRecCompanyResults([]);
    }
  };

  const handleCreate = async () => {
    setRecSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/recommendations`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_user_id: recSelectedUser.user_id,
          company_id: recSelectedCompany.company_id,
          note: recNote,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Recommendation created!');
      setRecSelectedUser(null); setRecSelectedCompany(null);
      setRecUserSearch(''); setRecCompanySearch(''); setRecNote('');
      const recsRes = await fetch(`${BACKEND_URL}/api/admin/recommendations`, { credentials: 'include' });
      if (recsRes.ok) { const d = await recsRes.json(); setAdminRecs(d.recommendations || []); }
    } catch (e) { toast.error(e.message); }
    setRecSubmitting(false);
  };

  const handleDelete = async (recId) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/recommendations/${recId}`, {
        method: 'DELETE', credentials: 'include',
      });
      setAdminRecs(prev => prev.filter(x => x.recommendation_id !== recId));
      toast.success('Recommendation removed');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-4">
      <AdminFeaturedRecsManager />
      <div className="flex items-center justify-between pt-4">
        <h2 className="text-xl font-bold text-slate-800">Company Recommendations</h2>
      </div>

      <Card className="p-5" data-testid="create-recommendation-form">
        <h3 className="text-sm font-bold text-slate-700 mb-3">Create New Recommendation</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-3">
          <div>
            <Label className="text-xs font-semibold text-slate-500">Select Member</Label>
            <Input
              placeholder="Search member by name or email..."
              value={recUserSearch}
              onChange={(e) => searchUsers(e.target.value)}
              data-testid="rec-user-search"
            />
            {recUserResults.length > 0 && !recSelectedUser && (
              <div className="border rounded-md mt-1 max-h-40 overflow-y-auto bg-white shadow-sm">
                {recUserResults.map(u => (
                  <button key={u.user_id} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                    onClick={() => { setRecSelectedUser(u); setRecUserSearch(u.name || u.email); setRecUserResults([]); }}
                    data-testid={`rec-user-option-${u.user_id}`}>
                    <User className="h-3.5 w-3.5 text-slate-400" />
                    <span className="font-medium">{u.name}</span>
                    <span className="text-xs text-slate-400">{u.email}</span>
                  </button>
                ))}
              </div>
            )}
            {recSelectedUser && (
              <div className="flex items-center gap-2 mt-1 px-2 py-1 bg-brand-navy/5 rounded text-sm">
                <User className="h-3.5 w-3.5 text-brand-navy" />
                <span className="font-medium text-brand-navy">{recSelectedUser.name}</span>
                <button onClick={() => { setRecSelectedUser(null); setRecUserSearch(''); }}
                  className="ml-auto text-slate-400 hover:text-red-500"><XIcon className="h-3.5 w-3.5" /></button>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">Select Company</Label>
            <Input
              placeholder="Search company by name..."
              value={recCompanySearch}
              onChange={(e) => searchCompanies(e.target.value)}
              data-testid="rec-company-search"
            />
            {recCompanyResults.length > 0 && !recSelectedCompany && (
              <div className="border rounded-md mt-1 max-h-40 overflow-y-auto bg-white shadow-sm">
                {recCompanyResults.map(c => (
                  <button key={c.company_id} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                    onClick={() => { setRecSelectedCompany(c); setRecCompanySearch(c.name); setRecCompanyResults([]); }}
                    data-testid={`rec-company-option-${c.company_id}`}>
                    <Building2 className="h-3.5 w-3.5 text-slate-400" />
                    <span className="font-medium">{c.name}</span>
                    {c.sector && <span className="text-xs text-slate-400">{c.sector}</span>}
                  </button>
                ))}
              </div>
            )}
            {recSelectedCompany && (
              <div className="flex items-center gap-2 mt-1 px-2 py-1 bg-brand-sage/10 rounded text-sm">
                <Building2 className="h-3.5 w-3.5 text-brand-sage" />
                <span className="font-medium text-brand-sage">{recSelectedCompany.name}</span>
                <button onClick={() => { setRecSelectedCompany(null); setRecCompanySearch(''); }}
                  className="ml-auto text-slate-400 hover:text-red-500"><XIcon className="h-3.5 w-3.5" /></button>
              </div>
            )}
          </div>
        </div>
        <div className="mb-3">
          <Label className="text-xs font-semibold text-slate-500">Note (optional)</Label>
          <Textarea
            value={recNote}
            onChange={e => setRecNote(e.target.value)}
            placeholder="Why you're recommending this company to this member..."
            className="min-h-[60px]"
            data-testid="rec-note"
          />
        </div>
        <Button
          disabled={!recSelectedUser || !recSelectedCompany || recSubmitting}
          onClick={handleCreate}
          className="bg-brand-navy text-white"
          data-testid="submit-recommendation-btn"
        >
          {recSubmitting ? 'Creating...' : 'Create Recommendation'}
        </Button>
      </Card>

      <Card className="p-5" data-testid="recommendations-list">
        <h3 className="text-sm font-bold text-slate-700 mb-3">All Recommendations ({adminRecs.length})</h3>
        {adminRecs.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">No recommendations yet. Create one above.</p>
        ) : (
          <div className="space-y-3">
            {adminRecs.map(r => (
              <div key={r.recommendation_id} className="flex items-center justify-between p-3 rounded-lg border bg-slate-50" data-testid={`admin-rec-${r.recommendation_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-brand-navy">{r.company_name}</span>
                    <span className="text-xs text-slate-400">recommended to</span>
                    <span className="text-sm font-semibold text-slate-700">{r.target_user_name}</span>
                  </div>
                  {r.note && <p className="text-xs text-slate-500 mt-0.5 italic">"{r.note}"</p>}
                  <p className="text-xs text-slate-400 mt-0.5">By {r.admin_name} &middot; {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <Button variant="ghost" size="sm"
                  onClick={() => handleDelete(r.recommendation_id)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-rec-${r.recommendation_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
